import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthProvider';
import AccueilScreen      from './screens/AccueilScreen';
import MissionDetailScreen from './screens/MissionDetailScreen';
import ConfirmationScreen  from './screens/ConfirmationScreen';
import { livraisonAPI, recuperationAPI } from './services/api';
import { Livraison, Recuperation } from './types';

type Screen = 'accueil' | 'detail' | 'confirmation';
type TypeMission = 'livraison' | 'recuperation';

/**
 * Cible d'ouverture demandée par une notification Web Push (PWA-01).
 *
 * Deux chemins, selon que la PWA était fermée ou seulement en arrière-plan :
 *  - fermée   → le Service Worker ouvre `/?mission=<id>&type=<type>`, lu ici au démarrage ;
 *  - ouverte  → il refocalise la fenêtre existante et envoie un message `OUVRIR_MISSION`,
 *               plutôt que d'ouvrir une seconde instance dont une seule porterait le flux SSE.
 *
 * Dans les deux cas, seuls un identifiant et un type circulent. La mission elle-même est
 * ensuite récupérée via l'API authentifiée : le payload d'une notification n'est jamais une
 * preuve d'autorisation, et c'est le backend — via les gardes d'appartenance de SEC-01 — qui
 * décide si ce chauffeur a le droit de voir cette mission.
 */
const lireCibleDeLUrl = (): { id: number; type: TypeMission } | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    const parametres = new URLSearchParams(window.location.search);
    const brut = parametres.get('mission');
    if (!brut) {
        return null;
    }
    const id = Number(brut);
    if (!Number.isSafeInteger(id) || id <= 0) {
        return null;
    }
    return { id, type: parametres.get('type') === 'recuperation' ? 'recuperation' : 'livraison' };
};

function AppInner() {
    const { isAuthenticated, isLoading, logout, keycloakUser } = useAuth();

    const [screen,  setScreen]  = useState<Screen>('accueil');
    const [mission, setMission] = useState<Livraison | Recuperation | null>(null);
    const [mType,   setMType]   = useState<TypeMission>('livraison');

    /**
     * Ouvre la mission désignée par une notification.
     *
     * La récupération passe par l'API authentifiée, donc par les contrôles d'appartenance
     * existants : un identifiant fabriqué à la main dans la barre d'adresse ne donne accès à
     * rien. En cas de refus ou d'erreur, on reste simplement sur l'accueil — un écran de détail
     * vide serait pire qu'un retour silencieux à la liste.
     */
    const ouvrirMission = useCallback(async (id: number, type: TypeMission) => {
        try {
            const reponse = type === 'recuperation'
                ? await recuperationAPI.getById(id)
                : await livraisonAPI.getById(id);
            if (reponse.success && reponse.data) {
                setMission(reponse.data as Livraison | Recuperation);
                setMType(type);
                setScreen('detail');
            }
        } catch {
            // Mission inaccessible ou disparue : l'accueil reste affiché.
        }
    }, []);

    // Ouverture depuis une PWA fermée : la cible est dans l'URL. Elle est retirée de la barre
    // d'adresse aussitôt lue, pour qu'un rechargement ultérieur ne rouvre pas indéfiniment la
    // même mission et que l'identifiant ne reste pas dans l'historique.
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        const cible = lireCibleDeLUrl();
        if (cible) {
            window.history.replaceState({}, '', window.location.pathname);
            void ouvrirMission(cible.id, cible.type);
        }
    }, [isAuthenticated, ouvrirMission]);

    // Ouverture depuis une PWA déjà lancée : le Service Worker envoie un message.
    useEffect(() => {
        if (!isAuthenticated || !('serviceWorker' in navigator)) {
            return undefined;
        }
        const surMessage = (event: MessageEvent) => {
            if (event.data?.type !== 'OUVRIR_MISSION') {
                return;
            }
            const id = Number(event.data.missionId);
            if (!Number.isSafeInteger(id) || id <= 0) {
                return;
            }
            void ouvrirMission(id, event.data.missionType === 'recuperation' ? 'recuperation' : 'livraison');
        };
        navigator.serviceWorker.addEventListener('message', surMessage);
        return () => navigator.serviceWorker.removeEventListener('message', surMessage);
    }, [isAuthenticated, ouvrirMission]);

    /* ── Chargement ── */
    if (isLoading) {
        return (
            <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                <div style={{ width: 56, height: 56, border: '4px solid #1e293b', borderTopColor: '#22c55e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ color: '#64748b', fontSize: 16, fontFamily: 'system-ui' }}>Connexion en cours...</p>
            </div>
        );
    }

    /* ── Non authentifié ── */
    if (!isAuthenticated) {
        return (
            <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24 }}>
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#f8fafc" strokeWidth="1.5" aria-hidden="true" focusable="false">
                    <rect x="5" y="11" width="14" height="9" rx="2"/>
                    <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                </svg>
                <h2 style={{ color: '#f8fafc', fontSize: 22, fontWeight: 800, margin: 0, textAlign: 'center' }}>Session expirée</h2>
                <button onClick={() => window.location.reload()}
                    style={{ background: '#22c55e', border: 'none', borderRadius: 16, padding: '20px 40px', color: '#000', fontSize: 18, fontWeight: 900, cursor: 'pointer', width: '100%', maxWidth: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" aria-hidden="true" focusable="false">
                        <path d="M3 12a9 9 0 1 1 3 6.7"/>
                        <path d="M3 17v-5h5"/>
                    </svg>
                    SE RECONNECTER
                </button>
            </div>
        );
    }

    /* ── Mauvais rôle ── */
    if (keycloakUser && !keycloakUser.roles.includes('CHAUFFEUR')) {
        return (
            <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24 }}>
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#f8fafc" strokeWidth="1.5" aria-hidden="true" focusable="false">
                    <circle cx="12" cy="12" r="9"/>
                    <path d="M5.5 5.5l13 13"/>
                </svg>
                <h2 style={{ color: '#f8fafc', fontSize: 20, fontWeight: 800, margin: 0, textAlign: 'center' }}>
                    Accès réservé aux chauffeurs
                </h2>
                <button onClick={logout}
                    style={{ background: '#ef4444', border: 'none', borderRadius: 14, padding: '18px 32px', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
                    Se déconnecter
                </button>
            </div>
        );
    }

    const prenom    = keycloakUser?.firstName || keycloakUser?.fullName?.split(' ')[0] || 'Chauffeur';
    const telephone = keycloakUser?.username || '';
    const conteneur = (mission as any)?.conteneur || (mission as any)?.numeroBL || '';

    const handleMissionSelect = (m: Livraison | Recuperation, t: 'livraison' | 'recuperation') => {
        setMission(m); setMType(t); setScreen('detail');
    };

    const handleSuccess = () => setScreen('confirmation');
    const handleRetour  = () => { setMission(null); setScreen('accueil'); };

    /* ── Routage simple ── */
    return (
        <>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulse { 0%,100% { box-shadow: 0 0 60px rgba(34,197,94,0.5); } 50% { box-shadow: 0 0 90px rgba(34,197,94,0.8); } }
                * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
                body { margin: 0; padding: 0; background: #0f172a; font-family: system-ui, -apple-system, sans-serif; }
                button { -webkit-tap-highlight-color: transparent; }
            `}</style>

            {screen === 'accueil' && (
                <AccueilScreen
                    prenom={prenom}
                    telephone={telephone}
                    onMissionSelect={handleMissionSelect}
                    onLogout={logout}
                />
            )}

            {screen === 'detail' && mission && (
                <MissionDetailScreen
                    mission={mission}
                    type={mType}
                    prenom={prenom}
                    onBack={() => setScreen('accueil')}
                    onSuccess={handleSuccess}
                />
            )}

            {screen === 'confirmation' && (
                <ConfirmationScreen
                    prenom={prenom}
                    conteneur={conteneur}
                    onRetour={handleRetour}
                />
            )}
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppInner />
        </AuthProvider>
    );
}

export default App;
